import { createSelector } from 'reselect'
import * as R from 'ramda'
import P from 'helpers/predicates'
import {
  getMonthDayYear
} from 'helpers/DateUtil'

const projectsStateSelector = state => state.project.greenlight.projects
const revisionsStateSelector = state => state.project.greenlight.revisions
const currentRevisionSelector = state => state.project.greenlight.currentRevision
const activeDialogStateSelector = state => state.project.greenlight.active
const actionTypeStateSelector = state => state.project.greenlight.actionType

const revisionSelector = createSelector(
  revisionsStateSelector,
  (revisions) => R.keys(revisions)
    .map((key) => ({ value: key, label: `Revision ${key}` }))
)

const revisionValueSelector = createSelector(
  revisionsStateSelector,
  currentRevisionSelector,
  (revisions, current) => R.ifElse(
    R.both(
      P.isNotNil,
      R.has
    ),
    R.prop,
    R.always(undefined)
  )(current, revisions)
)

const projectValueSelector = createSelector(
  projectsStateSelector,
  currentRevisionSelector,
  (projects, current) => R.ifElse(
    R.both(
      P.isNotNil,
      R.has
    ),
    R.prop,
    R.always(undefined)
  )(current, projects)
)

const systemInfoSelector = createSelector(
  revisionValueSelector,
  R.ifElse(
    R.both(
      P.isNotNil,
      R.has('info')
    ),
    R.prop('info'),
    R.always({})
  )
)

const gpuSelector = createSelector(
  revisionValueSelector,
  R.ifElse(
    R.both(
      P.isNotNil,
      R.has('gpu')
    ),
    R.prop('gpu'),
    R.always([])
  )
)

const submissionDateSelector = createSelector(
  revisionValueSelector,
  R.ifElse(
    R.both(
      P.isNotNil,
      R.has('updatedAt')
    ),
    R.compose(
      (date) => getMonthDayYear(new Date(date)),
      R.prop('updatedAt')
    ),
    R.always(undefined)
  )
)

const submissionBySelector = createSelector(
  revisionValueSelector,
  R.ifElse(
    R.both(
      P.isNotNil,
      R.has('createdBy')
    ),
    R.prop('createdBy'),
    R.always(undefined)
  )
)

const revisionNameSelector = createSelector(
  revisionValueSelector,
  R.ifElse(
    R.both(
      P.isNotNil,
      R.has('revisionName')
    ),
    R.prop('revisionName'),
    R.always(undefined)
  )
)


const ftsVersionSelector = createSelector(
  revisionValueSelector,
  R.ifElse(
    R.both(
      P.isNotNil,
      R.has('ftsVersion')
    ),
    R.prop('ftsVersion'),
    R.always(undefined)
  )
)

const resultSelector = createSelector(
  projectValueSelector,
  R.compose(
    R.ifElse(
      R.equals(true),
      R.always('Pass'),
      R.always('Fail')
    ),
    R.ifElse(
      P.isNotNil,
      R.all(R.propEq('result', 'pass')),
      R.always(false)
    )
  )
)

const fastTrackSourceSelector = createSelector(
  revisionValueSelector,
  R.ifElse(
    R.both(
      P.isNotNil,
      R.has('fastTrackSource')
    ),
    R.prop('fastTrackSource'),
    R.always(undefined)
  )
)

const reportSummarySelector = createSelector(
  resultSelector,
  submissionDateSelector,
  submissionBySelector,
  revisionNameSelector,
  fastTrackSourceSelector,
  (result, updatedAt, createdBy, revisionName, fastTrackSource) => ({
    result,
    updatedAt,
    createdBy,
    revisionName,
    fastTrackSource
  })
)

const approvedSelector = createSelector(
  revisionValueSelector,
  R.prop('approved')
)

export {
  projectValueSelector,
  currentRevisionSelector,
  revisionSelector,
  revisionValueSelector,
  approvedSelector,
  systemInfoSelector,
  gpuSelector,
  revisionsStateSelector,
  reportSummarySelector,
  activeDialogStateSelector,
  actionTypeStateSelector,
  ftsVersionSelector
}
